import PasswordRecovery from './components/auth/PasswordRecovery';
// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import AnalyticsObserver from './components/common/AnalyticsObserver';
import 'styles/ui-components.css';
import 'styles/ui-core.css';
import 'styles/ui-pages.css';

// Code Splitting for non-critical routes
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DriverTripsPage = lazy(() => import('./pages/DistributionPage'));
const DistributionPlanning = lazy(() => import('./pages/DistributionPage').then(m=>({default:m.DistributionPlanning})));
const BrowseTasks = lazy(() => import('./pages/BrowseTasks'));
const TaskDetail = lazy(() => import('./components/tasks/TaskDetail'));
const TaskCreationPage = lazy(() => import('./pages/CreateTask'));
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const AccountSetup = lazy(() => import('./components/auth/AccountSetup'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const CMRPage = lazy(() => import("./pages/legal/CMR"));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const TaskInstantDetail = lazy(() => import('./components/profile/TaskInstantDetail'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const StudioDashboard = lazy(() => import('./components/studio/StudioDashboard'));
const CarrierTermsPage = lazy(() => import('./pages/CarrierTermsPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const PaymentReturn = lazy(() => import('./pages/PaymentReturn'));


export default function App() {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const adminLogin = '/login?next=' + encodeURIComponent(location.pathname + location.search + location.hash);
  const isModerator = currentUser?.role === 'content_moderator';

  // WWW Redirect for SEO consolidation
  useEffect(() => {
    if (window.location.hostname.startsWith('www.')) {
      const newUrl = window.location.href.replace('www.', '');
      window.location.replace(newUrl);
    }
  }, []);

  if (loading) return <div role="status">Indlæser konto...</div>;

  return (
    <>
      <Layout>
        <Suspense fallback={<div style={{ padding: '50px', textAlign: 'center' }}>Indlæser...</div>}>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/min-tur" element={currentUser && !isModerator ? <DriverTripsPage/> : <Navigate to="/login?next=/min-tur" replace/>}/>
          <Route path="/distribution" element={<Navigate to="/create-task?category=normal&type=distribution" replace/>}/>
          <Route path="/koersel" element={currentUser ? <DistributionPlanning/> : <Navigate to="/login?next=/koersel" replace/>}/>
          <Route path="/dashboard" element={
            currentUser ? (isModerator ? <Navigate to="/studio" replace /> : <Dashboard />) : <Navigate to="/login?next=/dashboard" replace />} />

          <Route path="/BrowseTasks" element={isModerator ? <Navigate to="/studio" replace /> : <BrowseTasks />} />

          <Route path="/studio" element={
            currentUser && ['admin','content_moderator'].includes(currentUser.role) ? <StudioDashboard /> : (currentUser ? <Navigate to="/dashboard" replace /> : <Navigate to="/login?next=/studio" replace />)
          } />

          <Route path="/admin" element={currentUser && currentUser.role === 'admin' ? <AdminDashboard /> : (isModerator ? <Navigate to="/studio" replace /> : <Navigate to={currentUser ? '/dashboard' : adminLogin} replace />)} />
          <Route path="/admin/depots" element={currentUser && currentUser.role === 'admin' ? <AdminDashboard /> : (isModerator ? <Navigate to="/studio" replace /> : <Navigate to={currentUser ? '/dashboard' : adminLogin} replace />)} />
          <Route path="/AdminDashboard" element={isModerator ? <Navigate to="/studio" replace /> : <Navigate to="/admin" replace />} />
          <Route path="/tasks" element={isModerator ? <Navigate to="/studio" replace /> : (currentUser && (currentUser.role === 'carrier' || currentUser.role === 'admin') ? <BrowseTasks /> : <Navigate to={currentUser ? '/dashboard' : '/login?next=/tasks'} replace />)} />

          <Route path="/profile" element={isModerator ? <Navigate to="/studio" replace /> : <Profile />} />
          <Route path="/task/:id" element={currentUser ? (isModerator ? <Navigate to="/studio" replace /> : <TaskDetail />) : <Navigate to="/login" replace />} />
          <Route path="/task/instant/:id" element={currentUser ? (isModerator ? <Navigate to="/studio" replace /> : <TaskInstantDetail />) : <Navigate to="/login" replace />} />
          <Route path="/orders/:type/:id" element={currentUser ? (isModerator ? <Navigate to="/studio" replace /> : <OrderDetail />) : <Navigate to="/login" replace />} />

          <Route path="/create-task" element={isModerator ? <Navigate to="/studio" replace /> : <TaskCreationPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/konto-aktivering" element={<AccountSetup />} />
          <Route path="/reset-password" element={<PasswordRecovery />} />
          <Route path="/payment-success" element={<PaymentReturn />} />
          <Route path="/legal/cmr" element={<CMRPage />} />
          <Route path="/carrier-agreement" element={<CarrierTermsPage />} />
          <Route path="/carrier-terms" element={<Navigate to="/carrier-agreement" replace />} />
          <Route path="/terms-of-service" element={<TermsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/app/support" element={<SupportPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AnalyticsObserver />
        </Suspense>
      </Layout>
    </>
  );
}
